import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  DatetimeChangeEventDetail,
  IonDatetimeCustomEvent,
  IonToggleCustomEvent,
  ToggleChangeEventDetail,
} from '@ionic/core';
import moment from 'moment';
import { finalize } from 'rxjs';
import {
  CalendarEvent,
  DATETIMEFORMART,
  Place,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { BookingsStore } from 'src/app/stores/bookings.store';

@Component({
  selector: 'app-propose-new-time',
  templateUrl: './propose-new-time.component.html',
  styleUrls: ['./propose-new-time.component.scss'],
})
export class ProposeNewTimeComponent implements OnInit {
  readonly bookingsStore = inject(BookingsStore);
  readonly apiService = inject(ApiService);

  readonly onDismiss = output<string>();
  readonly meeting = input.required<CalendarEvent>();
  readonly place = input.required<Place>();

  protected readonly locale = inject(DatetimeFormatService)
    .selectedDateTimeFormat;

  readonly _meeting = signal<CalendarEvent | undefined>(undefined);

  protected readonly localStartTime = computed(() => {
    const meeting = this._meeting();
    return meeting?.startTime
      ? moment(meeting.startTime).format(DATETIMEFORMART)
      : '';
  });

  protected readonly localEndTime = computed(() => {
    const meeting = this._meeting();
    return meeting?.endTime
      ? moment(meeting.endTime).format(DATETIMEFORMART)
      : '';
  });

  protected readonly duration = computed(() => {
    const meeting = this._meeting();

    if (!meeting?.startTime || !meeting?.endTime) {
      return '00:00';
    }
    const start = new Date(meeting.startTime).getTime();
    const end = new Date(meeting.endTime).getTime();
    const diffMs = end - start;
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  });

  protected readonly maxAllowedDuration = computed(() => {
    let res = {
      hours: [] as number[],
      minutes: [] as number[],
    };
    const meeting = this._meeting();
    const startTimeToCompare = moment(meeting?.startTime);
    const endOfDay = moment(meeting?.startTime).endOf('day');
    const differenceInMinutes = endOfDay.diff(startTimeToCompare, 'minutes');
    let differenceInHours = Math.floor(differenceInMinutes / 60);
    if (differenceInMinutes % 60 === 0) differenceInHours -= 1;
    for (let i = 0; i <= differenceInHours; i++) {
      res.hours.push(i);
    }
    for (let i = 0; i <= differenceInMinutes % 60; i++) {
      res.minutes.push(i);
    }
    return res;
  });

  protected readonly loadingProposeNewTime = signal(false);
  protected readonly loadingCheckAvailability = signal(false);
  protected readonly placeAvailable = signal<null | boolean>(null);

  public ngOnInit() {
    this._meeting.set({ ...this.meeting() });
  }

  protected dismissModal(reason: string = '') {
    this.onDismiss.emit(reason);
  }


  protected handleTimeChange(
    event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>,
    type: 'startTime' | 'endTime',
  ) {
    const meeting = this._meeting();
    if (!meeting) return;
    const oldDuration =
      new Date(meeting.endTime).getTime() -
      new Date(meeting.startTime).getTime();

    if (type === 'startTime') {
      const startTime = moment(event.detail.value);
      meeting.startTime = startTime.utc().toISOString();
      if (meeting.isAllDay) {
        meeting.endTime = startTime.endOf('day').utc().toISOString();
      } else {
        meeting.endTime = startTime
          .add(oldDuration, 'milliseconds')
          .utc()
          .toISOString();
      }
    } else if (type === 'endTime') {
      const endTime = moment(event.detail.value);
      meeting.endTime = endTime.utc().toISOString();
    }
    this.placeAvailable.set(null);
    this._meeting.update((m) => ({ m, ...meeting }));
  }

  protected handleDurationChange(
    event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>,
  ) {
    const dur = event.detail.value;
    if (!dur) return;
    const durationMoment = moment.duration(Array.isArray(dur) ? dur[0] : dur);
    const startTime = moment(this._meeting()?.startTime);

    this._meeting.update((m) => {
      if (!m) return m;
      return {
        ...m,
        endTime: startTime.clone().add(durationMoment).utc().toISOString(),
      };
    });
    this.placeAvailable.set(null);
  }

  protected proposeNewTime() {
    const meeting = this._meeting();
    if (!meeting) return;
    this.loadingProposeNewTime.set(true);
    this.bookingsStore
      .tentativelyAcceptInvitationWithNewTime(
        meeting.id,
        false,
        meeting.startTime,
        meeting.endTime,
      )
      .pipe(finalize(() => this.loadingProposeNewTime.set(false)))
      .subscribe({
        next: () => {
          this.dismissModal('proposed');
        },
      });
  }

  protected checkAvailability() {
    const meeting = this._meeting();
    const oldMeeting = this.meeting();
    if (!meeting) return;
    this.loadingCheckAvailability.set(true);
    this.apiService
      .getOnePlaceAvailability(
        new Date(meeting.startTime),
        new Date(meeting.endTime),
        meeting.recurrencePattern,
        new Date(oldMeeting.startTime),
        new Date(oldMeeting.endTime),
        this.place(),
      )
      .pipe(
        finalize(() => {
          this.loadingCheckAvailability.set(false);
        }),
      )
      .subscribe({
        next: (isAvailable) => {
          this.placeAvailable.set(isAvailable);
        },
      });
  }
}
